import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { IMargins } from '@Common/Controls/Controls';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import * as commonUtils from '@Common/Helpers/CommonUtils';

export interface ICheckboxParameters extends IControlParameters {
    caption?: string;
    showCaption?: boolean;
    showControls?: boolean;
    blockElement?: boolean;
    inlineBlockElement?: boolean;
    checked?: boolean;
    margins?: IMargins;
    multiline?: boolean;
    tristate?: boolean;
    states?: (boolean | null)[];
    stopBubbling?: boolean;
    ignoreClickByLabel?: boolean;
}

export interface ISetValueParam {
    stopEvents: boolean;
}

export class CheckBoxParams implements ICheckboxParameters {
    public caption = '';
    public showCaption = true;
    public showControls = true;
    public blockElement = true;
    public inlineBlockElement = false;
    public checked: boolean = null;
    public margins: IApplyMargins = {
        left: false,
        right: false,
        top: false,
        bottom: false
    };

    public multiline = false;
    public tristate = false;
    public stopBubbling = true;
    public ignoreClickByLabel = false;
}

export class CheckBox extends BaseControl<ICheckboxParameters> {
    private label: JQuery;
    private isSelected = false;
    private readonly checkedClassName: string;
    private readonly lock = commonUtils.recursionLocker();
    private readonly checkBoxWidth = 25;

    private readonly events = new MulticastDelegateBag();
    public onChange = this.events.create<(state: boolean) => void>();

    constructor(param: ICheckboxParameters) {
        super(param);
        this._param = _.assignIn(new CheckBoxParams(), param);
        this.checkedClassName = this._param.showControls ? 'on' : 'check-on';
        this._init();
        this.value(this._param.checked);
    }

    protected _init(): void {
        this._root = this._param.blockElement ? $('<div class="checkbox"/>') : $('<span class="checkbox"/>');

        const span = $('<span/>').appendTo(this._root);

        this.label = $('<label/>')
            .text(this._param.caption)
            .filter(() => this._param.showCaption)
            .appendTo(this._root)
            .end();

        if (this._param.inlineBlockElement) {
            this._root.css('display', 'inline-block');
        }

        this._root.applyMargins(this._param.margins);

        const states = this._param.states ?? (this._param.tristate ? [null, true, false] : [true, false]);

        (this._param.ignoreClickByLabel ? span : this._root).click(e => {
            if (this._enabled && this._readonly !== true) {
                this.lock.lock(() => {
                    if (this._enabled !== false) {
                        const nextStateIndex = (states.indexOf(this.isSelected) + 1) % states.length;
                        this.value(states[nextStateIndex]);
                        if (this._param.stopBubbling) {
                            e.stopPropagation();
                        }
                    }
                });
            }
        });

        this._root.toggleClass('block', this._param.blockElement);

        if (this._param.multiline) {
            $('<div>')
                .css({
                    display: 'table-cell'
                })
                .insertBefore(span)
                .append(span);

            this.label.css({
                display: 'table-cell',
                whiteSpace: 'normal'
            });
        }

        this._applyDefaultParam();
    }

    public setCaption(caption: string): void {
        this.label.text(caption);
    }

    public adjust(width, height): void {
        super.adjust(width, this._param.multiline ? 'auto' : height);

        if (_.isString(this._param.width) && this._param.width.toLowerCase() === 'auto') {
            this.label.width('auto');
        } else {
            this.label.outerWidth(this._root.width() - this.checkBoxWidth);
        }
    }

    public setError(errorMessage: string): void {
        super.setError(errorMessage);
        this._root.addClass('checkbox-invalid');
        this._root.attr('title', errorMessage);
    }

    public removeError(): void {
        super.removeError();
        this._root.removeClass('checkbox-invalid');
        this._root.attr('title', '');
    }

    public value(selection?: boolean, setParam?: ISetValueParam): boolean {
        if (arguments.length === 0) {
            return this.isSelected;
        }
        if (this.isSelected !== selection) {
            if (this._param.tristate) {
                if (selection != null) {
                    this.isSelected = !!selection;
                } else {
                    this.isSelected = null;
                }
            } else {
                this.isSelected = !!selection;
            }

            if (!setParam || !setParam.stopEvents) {
                this.onChange(this.isSelected);
            }
        }

        this._root.toggleClass('undefined-check', this.isSelected === null);
        this._root.toggleClass(this.checkedClassName, this.isSelected === true);
        this._root.toggleClass('hidden', this.isSelected === false && !this._param.showControls);
        return this.isSelected;
    }

    public readonly(data: boolean): boolean {
        if (arguments.length === 0) {
            return this._readonly;
        }
        super.readonly(data);
        this._root.toggleClass('readonly-checkbox', this._readonly);
        return this._readonly;
    }
}
